import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import Button from '../common/Button';
import Modal from '../common/Modal';
import './Tasks.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Tasks = () => {
  const { token } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({
    title: '', vesselName: '', assignedTo: '', dueDate: '', description: '', status: 'pending'
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchTasks = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setTasks(data.tasks);
      } else {
        setError(data.message || 'Failed to load tasks');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Cannot connect to server. Please check if backend is running.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleSaveTask = async () => {
    if (!formData.title || !formData.assignedTo || !formData.dueDate) {
      alert('Please fill all required fields');
      return;
    }

    if (submitting) return;
    setSubmitting(true);

    const payload = {
      title: formData.title,
      description: formData.description,
      assignedTo: formData.assignedTo,
      dueDate: formData.dueDate,
      status: formData.status,
      vesselName: formData.vesselName || ''
    };

    try {
      const url = editingTask
        ? `${API_BASE}/tasks/${editingTask._id}`
        : `${API_BASE}/tasks`;
      const method = editingTask ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        await fetchTasks();
        setShowModal(false);
        setEditingTask(null);
        setFormData({ title: '', vesselName: '', assignedTo: '', dueDate: '', description: '', status: 'pending' });
      } else {
        alert(data.message || 'Error saving task');
      }
    } catch (err) {
      alert('Network error. Could not save task.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleComplete = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/tasks/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: 'completed' })
      });
      const data = await res.json();
      if (data.success) {
        fetchTasks();
      } else {
        alert(data.message);
      }
    } catch (err) {
      alert('Error updating task');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      const res = await fetch(`${API_BASE}/tasks/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        fetchTasks();
      } else {
        alert(data.message);
      }
    } catch (err) {
      alert('Error deleting task');
    }
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          task.vesselName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          task.assignedTo?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || task.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: tasks.length,
    pending: tasks.filter(t => t.status === 'pending').length,
    inProgress: tasks.filter(t => t.status === 'in_progress').length,
    completed: tasks.filter(t => t.status === 'completed').length
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending': return <span className="task-status-badge pending">⏳ Pending</span>;
      case 'in_progress': return <span className="task-status-badge in_progress">🔄 In Progress</span>;
      case 'completed': return <span className="task-status-badge completed">✅ Completed</span>;
      default: return <span className="task-status-badge">{status}</span>;
    }
  };

  if (loading) return <div className="loading-spinner">Loading tasks...</div>;
  if (error) return (
    <div className="error-message" style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>
      ⚠️ {error}<br />
      <button onClick={fetchTasks} style={{ marginTop: '16px', padding: '8px 16px', background: 'var(--primary)', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Retry</button>
    </div>
  );

  return (
    <div className="tasks-page">
      <div className="page-header">
        <div>
          <h1><i className="fas fa-tasks"></i> Tasks & Assignments</h1>
          <p>Manage and track crew assignments</p>
        </div>
        <Button variant="primary" icon="fa-plus" onClick={() => { setEditingTask(null); setFormData({ title: '', vesselName: '', assignedTo: '', dueDate: '', description: '', status: 'pending' }); setShowModal(true); }}>
          New Task
        </Button>
      </div>

      <div className="stats-grid">
        <div className="stat-card"><div className="stat-icon"><i className="fas fa-tasks"></i></div><div className="stat-info"><div className="stat-value">{stats.total}</div><div className="stat-label">Total Tasks</div></div></div>
        <div className="stat-card"><div className="stat-icon"><i className="fas fa-clock"></i></div><div className="stat-info"><div className="stat-value">{stats.pending}</div><div className="stat-label">Pending</div></div></div>
        <div className="stat-card"><div className="stat-icon"><i className="fas fa-spinner"></i></div><div className="stat-info"><div className="stat-value">{stats.inProgress}</div><div className="stat-label">In Progress</div></div></div>
        <div className="stat-card"><div className="stat-icon"><i className="fas fa-check-circle"></i></div><div className="stat-info"><div className="stat-value">{stats.completed}</div><div className="stat-label">Completed</div></div></div>
      </div>

      <div className="filters-bar">
        <div className="filter-tabs">
          <button className={`filter-tab ${filterStatus === 'all' ? 'active' : ''}`} onClick={() => setFilterStatus('all')}>All</button>
          <button className={`filter-tab ${filterStatus === 'pending' ? 'active' : ''}`} onClick={() => setFilterStatus('pending')}>Pending</button>
          <button className={`filter-tab ${filterStatus === 'in_progress' ? 'active' : ''}`} onClick={() => setFilterStatus('in_progress')}>In Progress</button>
          <button className={`filter-tab ${filterStatus === 'completed' ? 'active' : ''}`} onClick={() => setFilterStatus('completed')}>Completed</button>
        </div>
        <div className="search-box"><i className="fas fa-search"></i><input type="text" placeholder="Search tasks..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /></div>
      </div>

      {filteredTasks.length === 0 ? (
        <div className="empty-state"><i className="fas fa-check-circle"></i><h3>No tasks found</h3><p>Create a new task to get started</p></div>
      ) : (
        <div className="tasks-grid">
          {filteredTasks.map(task => (
            <div key={task._id} className="task-card">
              <div className="task-header">
                <div className="task-info"><h4>{task.title}</h4><div className="task-id">ID: {task._id.slice(-8)}</div></div>
                {getStatusBadge(task.status)}
              </div>
              <div className="task-body">
                <p className="task-description">{task.description}</p>
                <div className="task-details">
                  <div className="detail-row"><i className="fas fa-ship"></i><span>{task.vesselName || 'N/A'}</span></div>
                  <div className="detail-row"><i className="fas fa-user"></i><span>Assigned to: {task.assignedTo}</span></div>
                  <div className="detail-row"><i className="fas fa-calendar"></i><span>Due: {new Date(task.dueDate).toLocaleDateString()}</span></div>
                </div>
                <div className="task-footer">
                  {task.status !== 'completed' && (
                    <button className="task-btn complete" onClick={() => handleComplete(task._id)}><i className="fas fa-check"></i> Complete</button>
                  )}
                  <button className="task-btn" onClick={() => { setEditingTask(task); setFormData({ title: task.title, vesselName: task.vesselName || '', assignedTo: task.assignedTo, dueDate: task.dueDate.split('T')[0], description: task.description || '', status: task.status }); setShowModal(true); }}><i className="fas fa-edit"></i> Edit</button>
                  <button className="task-btn" onClick={() => handleDelete(task._id)}><i className="fas fa-trash"></i> Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingTask ? 'Edit Task' : 'Create New Task'}>
        <div className="form-group"><label>Title *</label><input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} /></div>
        <div className="form-group"><label>Vessel Name</label><input type="text" value={formData.vesselName} onChange={(e) => setFormData({...formData, vesselName: e.target.value})} /></div>
        <div className="form-group"><label>Assigned To *</label><input type="text" value={formData.assignedTo} onChange={(e) => setFormData({...formData, assignedTo: e.target.value})} /></div>
        <div className="form-group"><label>Due Date *</label><input type="date" value={formData.dueDate} onChange={(e) => setFormData({...formData, dueDate: e.target.value})} /></div>
        <div className="form-group"><label>Description</label><textarea rows="3" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} /></div>
        <div className="form-group"><label>Status</label><select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})}><option value="pending">Pending</option><option value="in_progress">In Progress</option><option value="completed">Completed</option></select></div>
        <div className="modal-actions"><Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button><Button variant="primary" onClick={handleSaveTask} disabled={submitting}>{submitting ? 'Saving...' : (editingTask ? 'Update' : 'Create')}</Button></div>
      </Modal>
    </div>
  );
};

export default Tasks;